import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from '@wix/yoshi-flow-editor';
import { useGenericEventsLogger } from '@wix/data-tools-generic-events';
import { useSettings } from '@wix/tpa-settings/react';
import {
  Button,
  ButtonPriority,
  Divider,
  PaddingModes,
  Tags,
  TagsAlignment,
  TagsSize,
  TagsSkin,
} from 'wix-ui-tpa/cssVars';
import { ChevronLeft, ChevronRight } from '@wix/wix-ui-icons-common';
import { classes } from './FiltersBar.st.css';
import { FiltersModal } from './FiltersModal';
import { RentalsListDataHooks } from '../dataHooks';
import settingsParams from '../../settingsParams';
import { countActiveFilters } from '../../../../utils/buildFilters';
import type { FilterBarState, FilterData } from '../../../../types/types';

export type FiltersBarProps = {
  filterState: FilterBarState;
  filterData: FilterData;
  onChange: (next: FilterBarState) => void;
  disabled?: boolean;
  popoverLayer: HTMLElement | null;
};

export const FiltersBar: FC<FiltersBarProps> = ({
  filterState,
  filterData,
  onChange,
  disabled,
  popoverLayer,
}) => {
  const { t } = useTranslation();
  const logger = useGenericEventsLogger();
  const settings = useSettings();
  const showFiltersButton = settings.get(settingsParams.showFiltersButton);
  const showFilterTags = settings.get(settingsParams.showFilterTags);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [isScrollable, setIsScrollable] = useState(false);
  const activeCount = countActiveFilters(filterState);

  const toggleResourceType = (id: string) => {
    const current = filterState.resourceTypeIds;
    const next = current.includes(id)
      ? current.filter((x) => x !== id)
      : [...current, id];
    logger.userInput({
      componentName: 'resource_type_filter',
      inputNewValue: next.join(','),
      inputPrevValue: current.join(','),
      listSize: filterData.resourceTypes.length,
    });
    onChange({ ...filterState, resourceTypeIds: next });
  };

  const hasResourceTypes = filterData.resourceTypes.length > 0;

  /* Whether the row overflows at all — each arrow then scrolls its own
     physical direction and the browser clamps at either end, so nothing here
     has to reason about LTR vs RTL. */
  useEffect(() => {
    const element = scrollRef.current;

    if (!element) {
      return;
    }

    const observer = new ResizeObserver(() =>
      setIsScrollable(element.scrollWidth > element.clientWidth + 1),
    );
    observer.observe(element);

    return () => observer.disconnect();
  }, [filterData.resourceTypes]);

  const page = (offset: number) =>
    scrollRef.current?.scrollBy({ left: offset, behavior: 'smooth' });

  return (
    <div className={classes.root} data-hook={RentalsListDataHooks.filtersBar}>
      {/*
        `paddingMode="explicitPaddings"` is what makes this sizable: without it
        the Button is `box-sizing: content-box` and takes its padding from a
        shorthand, so the `--wix-ui-tpa-button-padding-*` vars are ignored and an
        outside `height` becomes height + padding + border.
      */}
      {showFiltersButton ? (
        <Button
          priority={ButtonPriority.secondary}
          paddingMode={PaddingModes.explicitPaddings}
          className={classes.filtersButton}
          data-hook={RentalsListDataHooks.filtersButton}
          onClick={() => {
            logger.clickCta({ componentName: 'filters_button' });
            setIsModalOpen(true);
          }}
          disabled={disabled}
        >
          <span className={classes.filtersIcon} aria-hidden="true" />
          {t('widget.servicesList.filters.filtersButton')}
          {activeCount > 0 && (
            <span
              className={classes.filtersCount}
              aria-label={t(
                'widget.servicesList.filters.filtersAppliedAriaLabel',
                {
                  count: activeCount,
                },
              )}
            >
              {activeCount}
            </span>
          )}
        </Button>
      ) : null}

      {showFilterTags && hasResourceTypes && (
        <>
          {showFiltersButton ? (
            <Divider
              className={classes.divider}
              direction="vertical"
              ariaHidden
            />
          ) : null}
          {/*
            Horizontally scrollable rather than wrapping: the production widget
            keeps the bar to a single row however many resource types exist.
          */}
          <div className={classes.chipsViewport}>
            {isScrollable ? (
              <button
                type="button"
                className={classes.scrollArrow}
                data-hook={RentalsListDataHooks.resourceTypeScrollStart}
                aria-label={t('widget.servicesList.filters.scrollLeft')}
                onClick={() => page(-200)}
              >
                <ChevronLeft />
              </button>
            ) : null}

            <div
              ref={scrollRef}
              className={classes.chipsScroll}
              data-hook={RentalsListDataHooks.resourceTypeFilter}
            >
              <Tags
                className={classes.chips}
                size={TagsSize.medium}
                alignment={TagsAlignment.left}
                skin={TagsSkin.light}
                items={filterData.resourceTypes.map((type) => ({
                  title: type.name,
                  value: type.id,
                  checked: filterState.resourceTypeIds.includes(type.id),
                  'aria-label': type.name,
                }))}
                onClick={(item) => toggleResourceType(item.value)}
              />
            </div>

            {isScrollable ? (
              <button
                type="button"
                className={classes.scrollArrow}
                data-hook={RentalsListDataHooks.resourceTypeScrollEnd}
                aria-label={t('widget.servicesList.filters.scrollRight')}
                onClick={() => page(200)}
              >
                <ChevronRight />
              </button>
            ) : null}
          </div>
        </>
      )}

      <FiltersModal
        popoverLayer={popoverLayer}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        filterState={filterState}
        attributeDefinitions={filterData.attributeDefinitions}
        onApply={(next) => {
          onChange(next);
          setIsModalOpen(false);
        }}
      />
    </div>
  );
};
